import {
  Box,
  Card,
  Flex,
  Form,
  Icon,
  Text,
  Button,
  Heading,
  CardBody,
  Textarea,
  IconButton,
  FormControl,
  FormErrorMessage,
  Stack,
  StackDivider,
  useToast,
  Link
} from '@homebotapp/hb-react-component-catalog'
import Phone from '../../../shared/Phone/Phone'
import Email from '../../../shared/Email/Email'
import { selectFullName, selectScheduleACallUri } from '../../../../store/selectors/customerProfile'
import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import useViewport from '../../../../hooks/useViewport'
import { ClientEventSource, Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { flashNotification } from '../../../../actions/notification'
import { HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE } from '../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { MessageTopicKey } from '../../../../api/gqlaxy/gql/generated/graphql'
import { useSendListingMessage } from '../../../../hooks/listings/useSendListingMessage'
import { hasCustomerToken } from '../../../../auth'
import { ClientTeamMembership } from '../../../../types/clientTeamMembership'
import { AppDispatch } from '../../../../store'
import { AvatarBubblesSmall } from './AvatarBubblesSmall/AvatarBubblesSmall'
import { LeavingHomebotCtaButton } from '../../../shared/LeavingHomebotCta/LeavingHomebotCtaButton'
import { useVerificationFlowEnabled } from '../../../../hooks/useVerificationFlowEnabled'
import { useSafeVerificationFlow } from '../../../shared/Questions/hooks/useSafeVerificationFlow'
import { useVerificationFlow } from '../../../shared/VerificationFlow'
import { CUSTOMER_MESSAGES } from '../../../../constants/customerMessages'

export const MSG = defineMessages({
  head: {
    id: 'ListingDetails.VerifiedAgentCard.head',
    defaultMessage: 'Ask a question'
  },
  headHidden: {
    id: 'ListingDetails.VerifiedAgentCard.headHidden',
    defaultMessage: 'Have a question'
  },
  submit: {
    id: 'ListingDetails.VerifiedAgentCard.submit',
    defaultMessage: 'Send message'
  },
  defaultQuestion: {
    id: 'ListingDetails.VerifiedAgentCard.defaultQuestion',
    defaultMessage: 'I would like to learn more about this listing.'
  },
  sentToReaAndLO: {
    id: 'ListingDetails.VerifiedAgentCard.removeOrReplace',
    defaultMessage: 'This message will be sent directly to {agentName} and {loanOfficerName}'
  },
  scheduleACallHeaderText: {
    id: 'ListingDetails.ListingAgentCard.scheduleACallHeaderText',
    defaultMessage: 'Schedule a call'
  },
  withPreferredAgent: {
    id: 'ListingDetails.VerifiedAgentCard.withPreferredAgent',
    defaultMessage: 'with {agentName}'
  },
  fail: {
    id: 'ListingDetails.VerifiedAgentCard.fail',
    defaultMessage: 'Message failed to send'
  },
  required: {
    id: 'ListingDetails.VerifiedAgentCard.required',
    defaultMessage: 'Required'
  },
  show: {
    id: 'ListingDetails.VerifiedAgentCard.show',
    defaultMessage: 'Show agent contact card'
  },
  hide: {
    id: 'ListingDetails.VerifiedAgentCard.hide',
    defaultMessage: 'Hide agent contact card'
  },
  sendMsgImpersonating: {
    id: 'ListingDetails.VerifiedAgentCard.sendMsgImpersonation',
    defaultMessage: 'Sending a message on behalf of your clients is not supported at this time.'
  },
  messageSent: {
    id: 'ListingDetails.VerifiedAgentCard.messageSent',
    defaultMessage: 'Message sent!'
  },
  continueText: {
    id: 'ListingDetails.VerifiedAgentCard.continueText',
    defaultMessage: "Do you want to continue to {name}'s scheduling site in a new tab?"
  }
})

export interface VerifiedAgentCardProps {
  listing?: Listing
  clientTeamMembership: ClientTeamMembership
}

export interface MessageFormValues {
  question: string
}

export const VerifiedAgentCard = ({ listing, clientTeamMembership }: VerifiedAgentCardProps) => {
  const intl = useIntl()
  const dispatch = useDispatch<AppDispatch>()
  const customerName = useSelector(selectFullName)
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const { startVerification, state: verificationState } = useSafeVerificationFlow()
  const { dispatch: verificationDispatch } = useVerificationFlow()

  const { isLargeOrAbove } = useViewport()
  const [isHidden, setIsHidden] = useState(false)

  const firstName = clientTeamMembership.customerProfile?.firstName
  const lastName = clientTeamMembership.customerProfile?.lastName
  const agentName = firstName && lastName ? `${firstName} ${lastName}` : null
  const appointmentLinkUri = clientTeamMembership.customerProfile?.appointmentLinkUri

  const toast = useToast()
  const isImpersonating = hasCustomerToken()

  useEffect(() => {
    if (isLargeOrAbove) {
      setIsHidden(false)
    } else {
      setIsHidden(true)
    }
  }, [isLargeOrAbove])

  const { mutateAsync: sendListingMessage, isLoading: isSendingListingMessage } = useSendListingMessage()

  const handleSubmit = async (data: MessageFormValues) => {
    if (isImpersonating) {
      return toast({
        description: intl.formatMessage(MSG.sendMsgImpersonating),
        status: 'warning',
        position: 'top'
      })
    }

    if (!listing?.id) {
      return dispatch(flashNotification({ message: intl.formatMessage(MSG.fail), messageType: 'error' }))
    }

    try {
      await sendListingMessage({
        input: {
          message: data.question,
          listingId: listing.id,
          topicKey: MessageTopicKey.ListingsDirectMessageToHomebotNetworkPreferredAgent,
          sendToPreferredAgent: true
        }
      })

      toast({
        title: intl.formatMessage(MSG.messageSent),
        status: 'success'
      })

      if (isVerificationFlowEnabled) {
        // Start verification flow for LO prequal after RE message success
        startVerification(
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.message.defaultMessage,
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey,
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey, // Using same topic for both verified and default
          'LoanOfficer'
        )
      }
    } catch (err) {
      Sentry.captureMessage('DM failed to send', {
        extra: {
          // @ts-ignore
          error: err.message || '',
          // @ts-ignore
          requestData: err.config?.data || {},
          // @ts-ignore
          requestErrors: Object.values(err?.response?.data?.errors || {})
        }
      })
      return dispatch(flashNotification({ message: intl.formatMessage(MSG.fail), messageType: 'error' }))
    }
  }

  const scheduleACallRenderModalBody = () => {
    return (
      <Text>
        {intl.formatMessage(MSG.continueText, {
          name: customerName
        })}
      </Text>
    )
  }

  return (
    <>
      {/* @ts-ignore */}
      <Card size={'lg'} backgroundColor='var(--hb-colors-primary-50) !important'>
        <CardBody p={4}>
          <Flex justify='space-between' align='center'>
            <Stack direction='column' spacing='0' justifyContent='space-between'>
              <Heading
                textAlign='left'
                mb={0}
                size={isHidden ? '3xs' : 'xs'}
                variant={isHidden ? 'eyebrow' : undefined}
                fontWeight='semibold'
                color={isHidden ? 'primary.700' : 'neutral.700'}
                textTransform='uppercase'
              >
                {isHidden ? intl.formatMessage(MSG.headHidden) : intl.formatMessage(MSG.head)}
              </Heading>
              {!isHidden && (
                <Text size='md' fontWeight='semibold' color='neutral.900' mb='0' mt='2'>
                  {clientTeamMembership.customerProfile.firstName} {clientTeamMembership.customerProfile.lastName}
                </Text>
              )}

            </Stack>
            {!isHidden && (
              <AvatarBubblesSmall
                photoUri={clientTeamMembership.customerProfile.photoUri}
                companyLogo={clientTeamMembership.corporateProfile?.avatarUri}
              />
            )}

            <IconButton
              size='xs'
              display={['flex', null, null, 'none']}
              onClick={() => setIsHidden(!isHidden)}
              icon={<Icon name={isHidden ? 'angle-up' : 'angle-down'} />}
              aria-label={intl.formatMessage(isHidden ? MSG.show : MSG.hide)}
              tracking={{
                guid: 'qEeYcoVW3GGzsbrAoXKFnT',
                ui_context: HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE,
                descriptor: isHidden ? MSG.show.defaultMessage : MSG.hide.defaultMessage
              }}
            />
          </Flex>

          {/* Agent Contact Information Row */}
          {!isHidden && (
            <Stack direction='column' spacing='0' mt='3' mb='3'>
              {clientTeamMembership.corporateProfile?.name && (
                <Text size='2xs' color='neutral.700' mb='0' fontWeight='semibold'>
                  {clientTeamMembership.corporateProfile.name}
                </Text>
              )}
              <Flex direction='row' wrap='wrap' gap={1}>
                {clientTeamMembership.customerProfile.stateLicense && (
                  <Text size='2xs' color='neutral.700' mb='0'>
                    License #{clientTeamMembership.customerProfile.stateLicense}
                  </Text>
                )}
                {clientTeamMembership.customerProfile.stateLicense && clientTeamMembership.customerProfile.nmlsId && (
                  <Text size='2xs' color='neutral.700' mb='0'>
                    {' • '}
                  </Text>
                )}
                {clientTeamMembership.customerProfile.nmlsId && (
                  <Text size='2xs' color='neutral.700' mb='0'>
                    MLS #{clientTeamMembership.customerProfile.nmlsId}
                  </Text>
                )}
              </Flex>
              {clientTeamMembership.customerProfile.phone && (
                <Text size='2xs' mb='0'>
                  <Phone
                    value={clientTeamMembership.customerProfile.phone}
                    style={{ padding: 0 }}
                  />
                </Text>
              )}
              {clientTeamMembership.customerProfile.email && (
                <Text size='2xs' mb='0'>
                  <Email
                    value={clientTeamMembership.customerProfile.email}
                    style={{ padding: 0 }}
                  />
                </Text>
              )}
            </Stack>
          )}

          {!isHidden && (
            <>
              <Form
                onSubmit={handleSubmit}
                values={{
                  question: intl.formatMessage(MSG.defaultQuestion)
                }}
              >
                {({ formState: { errors, isValid } }) => (
                  <>
                    <FormControl isInvalid={!!errors?.question} mb={2}>
                      <Textarea
                        size='md'
                        name='question'
                        borderRadius={8}
                        color='neutral.900'
                        aria-describedby='agent-question-desc'
                        rules={{ required: intl.formatMessage(MSG.required) }}
                      />
                      <FormErrorMessage>{errors?.question?.message?.toString()}</FormErrorMessage>
                    </FormControl>
                    <Button
                      my='4'
                      w='100%'
                      type='submit'
                      isDisabled={!isValid}
                      isLoading={isSendingListingMessage}
                      colorScheme='primary'
                      tracking={{
                        guid: 'mUNEDPT3diD6b3CzRgASQP',
                        ui_context: HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE,
                        descriptor: MSG.submit.defaultMessage
                      }}
                    >
                      {intl.formatMessage(MSG.submit)}
                    </Button>
                    <Stack divider={<StackDivider />}>
                      <Text size='3xs' color='neutral.600'>
                        {intl.formatMessage(MSG.sentToReaAndLO, { agentName, loanOfficerName: customerName })}
                      </Text>
                      {!isImpersonating && appointmentLinkUri && (
                        <Flex mt='3' gap={1} alignItems='center' justifyContent='center'>
                          <LeavingHomebotCtaButton
                            scheduleACall
                            ctaButtonSize='sm'
                            ctaButtonColorScheme='brand'
                            hideIllustration
                            eventSource={ClientEventSource.ListingDetailed}
                            headerText={intl.formatMessage(MSG.scheduleACallHeaderText)}
                            ctaButtonVariant='link'
                            ctaLinkUrl={appointmentLinkUri}
                            renderModalBody={scheduleACallRenderModalBody}
                            tracking={{
                              guid: 'eDaqwAZ4sFJVmmWmTpMcQR',
                              ui_context: HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE,
                              descriptor: 'Schedule a call button clicked, Team card'
                            }}
                          >
                            {intl.formatMessage(MSG.scheduleACallHeaderText)}
                          </LeavingHomebotCtaButton>
                          <Text size='xs' textDecoration='none' mb='0' fontWeight='semibold'>
                            {intl.formatMessage(MSG.withPreferredAgent, {
                              agentName: agentName
                            })}
                          </Text>
                        </Flex>
                      )}
                    </Stack>
                  </>
                )}
              </Form>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
